import { EMBEDDED_BASE_URL } from "./constants"
import {
  createIframe,
  removeIframe,
  type EmbeddedPresentation,
} from "./iframe"
import { createParentBridge, type ParentBridge } from "./bridge"
import type { EmbeddedLayout, StartParams } from "./types"

// Showcase one-tag embed autoboot:
//
//   <script async src=".../palazzo-showcase-embed.js"
//     data-partner-slug="serhant"
//     data-layout="auto"
//     data-presentation="panel"></script>
//
// Renders a floating launcher button. On click:
//   1. Authorize via the Showcase public-authorize proxy
//   2. Open a local file picker for the room photo
//   3. Upload the photo via the embedded image-upload endpoint
//   4. Open the Showcase chat-embedded iframe, as a docked panel by default
//      and fullscreen when data-presentation="fullscreen" or the viewport is
//      too narrow for a panel

export type LauncherPosition = "bottom-right" | "bottom-left"

export interface ShowcaseAutobootConfig {
  partnerSlug: string
  authorizeUrl: string
  presentation: EmbeddedPresentation
  /** Null when data-layout omitted — use partner defaultLayout from authorize. */
  layout: EmbeddedLayout | null
  position: LauncherPosition
  widgetBaseUrl: string
}

const BOOTED_FLAG = "__palazzoShowcaseBooted"
const LAUNCHER_ATTR = "data-palazzo-showcase-launcher"
const VISITOR_KEY_PREFIX = "palazzo-showcase:visitor:"
const PUBLIC_AUTHORIZE_PATH = "/api/showcase-embed/public-authorize"
const UPLOAD_IMAGE_PATH = "/api/showcase-embed/upload-image"

const LAUNCHER_ICON_SVG =
  '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" ' +
  'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'

function parseLayout(raw: string | null): EmbeddedLayout {
  const valid: EmbeddedLayout[] = ["mobile", "desktop", "auto"]
  return valid.find((v) => v === raw) ?? "auto"
}

function parseLauncherPosition(raw: string | null): LauncherPosition {
  return raw === "bottom-left" ? "bottom-left" : "bottom-right"
}

export function parsePresentation(raw: string | null): EmbeddedPresentation {
  return raw === "fullscreen" ? "fullscreen" : "panel"
}

function originFromScriptSrc(script: {
  getAttribute(name: string): string | null
}): string | null {
  const src = script.getAttribute("src")?.trim()
  if (!src) return null
  try {
    return new URL(
      src,
      typeof window !== "undefined" ? window.location.href : undefined,
    ).origin
  } catch {
    return null
  }
}

export function parseShowcaseConfig(
  script: { getAttribute(name: string): string | null } | null,
  widgetBaseUrl: string = EMBEDDED_BASE_URL,
): ShowcaseAutobootConfig | null {
  if (!script) return null
  const partnerSlug = script.getAttribute("data-partner-slug")?.trim()
  if (!partnerSlug) return null

  const configuredBaseUrl =
    script.getAttribute("data-widget-base-url")?.trim() ||
    originFromScriptSrc(script) ||
    widgetBaseUrl
  const effectiveBaseUrl =
    configuredBaseUrl && configuredBaseUrl !== "undefined"
      ? configuredBaseUrl.replace(/\/$/, "")
      : null
  if (!effectiveBaseUrl) {
    console.error(
      "[ShowcaseSDK] could not resolve the widget base url; ignoring embed",
    )
    return null
  }

  const authorizeUrl =
    script.getAttribute("data-authorize-url")?.trim() ||
    `${effectiveBaseUrl}${PUBLIC_AUTHORIZE_PATH}`

  const layoutAttr = script.getAttribute("data-layout")
  return {
    partnerSlug,
    authorizeUrl,
    presentation: parsePresentation(script.getAttribute("data-presentation")),
    layout: layoutAttr ? parseLayout(layoutAttr) : null,
    position: parseLauncherPosition(script.getAttribute("data-position")),
    widgetBaseUrl: effectiveBaseUrl,
  }
}

function readVisitorId(partnerSlug: string): string | null {
  try {
    return window.localStorage.getItem(`${VISITOR_KEY_PREFIX}${partnerSlug}`)
  } catch {
    return null
  }
}

function storeVisitorId(partnerSlug: string, visitorId: string) {
  try {
    window.localStorage.setItem(`${VISITOR_KEY_PREFIX}${partnerSlug}`, visitorId)
  } catch {
    // localStorage unavailable — the server mints a fresh visitor per
    // authorize; the widget still works, just without cross-session identity.
  }
}

async function authorize(
  config: ShowcaseAutobootConfig,
): Promise<{
  token: string
  visitorId?: string
  defaultLayout?: EmbeddedLayout
} | null> {
  const body: Record<string, string> = {
    partnerSlug: config.partnerSlug,
    parentOrigin: window.location.origin,
  }
  const storedVisitor = readVisitorId(config.partnerSlug)
  if (storedVisitor) body.visitorId = storedVisitor

  try {
    const res = await fetch(config.authorizeUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "omit",
      body: JSON.stringify(body),
    })
    if (!res.ok) return null
    const data = await res.json()
    if (!data?.token) return null
    if (typeof data.visitorId === "string") {
      storeVisitorId(config.partnerSlug, data.visitorId)
    }
    const valid: EmbeddedLayout[] = ["mobile", "desktop", "auto"]
    const defaultLayout = valid.find((v) => v === data.defaultLayout)
    return {
      token: data.token,
      visitorId: data.visitorId,
      defaultLayout,
    }
  } catch {
    return null
  }
}

async function uploadImageFile(
  file: File,
  token: string,
  widgetBaseUrl: string,
): Promise<string | null> {
  const formData = new FormData()
  formData.append("file", file)
  try {
    const res = await fetch(`${widgetBaseUrl}${UPLOAD_IMAGE_PATH}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-embedded-parent-origin": window.location.origin,
      },
      body: formData,
    })
    if (!res.ok) return null
    const data = await res.json()
    const payload = data?.data ?? data
    return payload?.imageUrl ?? payload?.url ?? null
  } catch {
    return null
  }
}

function pickFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.style.display = "none"
    input.addEventListener("change", () => {
      const file = input.files?.[0] ?? null
      input.remove()
      resolve(file)
    })
    input.addEventListener("cancel", () => {
      input.remove()
      resolve(null)
    })
    document.body.appendChild(input)
    input.click()
  })
}

function createLauncher(position: LauncherPosition): {
  container: HTMLDivElement
  button: HTMLButtonElement
} {
  const container = document.createElement("div")
  container.setAttribute(LAUNCHER_ATTR, "")
  container.style.cssText = [
    "position:fixed",
    "bottom:24px",
    position === "bottom-left" ? "left:24px" : "right:24px",
    "z-index:2147483645",
  ].join(";")

  const button = document.createElement("button")
  button.setAttribute("type", "button")
  button.setAttribute("aria-label", "Design in your room")
  button.style.cssText = [
    "width:56px",
    "height:56px",
    "border-radius:50%",
    "border:0",
    "background:#1c1917",
    "color:#fff",
    "cursor:pointer",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "padding:0",
    "box-shadow:0 8px 24px rgba(0,0,0,0.28)",
    "transition:transform 120ms ease",
  ].join(";")
  button.innerHTML = LAUNCHER_ICON_SVG
  button.addEventListener("mouseenter", () => {
    button.style.transform = "scale(1.08)"
  })
  button.addEventListener("mouseleave", () => {
    button.style.transform = ""
  })

  container.appendChild(button)
  return { container, button }
}

export function bootShowcaseLauncher(config: ShowcaseAutobootConfig): void {
  let iframe: HTMLIFrameElement | null = null
  let overlay: HTMLDivElement | null = null
  let bridge: ParentBridge | null = null
  let opening = false

  const { container, button } = createLauncher(config.position)

  const showLauncher = () => { container.style.display = "" }
  const hideLauncher = () => { container.style.display = "none" }

  function teardown() {
    bridge?.dispose()
    if (iframe) removeIframe(iframe, overlay)
    iframe = null
    overlay = null
    bridge = null
  }

  function startEmbed(
    token: string,
    imageUrl: string,
    layout: EmbeddedLayout,
  ) {
    const chatId = crypto.randomUUID()
    const parentOrigin = window.location.origin
    const layoutParam = `&layout=${encodeURIComponent(layout)}`
    const url =
      `${config.widgetBaseUrl}/chat-embedded/${chatId}` +
      `?p=${encodeURIComponent(config.partnerSlug)}` +
      `&o=${encodeURIComponent(parentOrigin)}` +
      `&auth=${encodeURIComponent(token)}` +
      layoutParam +
      `#imageUrl=${encodeURIComponent(imageUrl)}`

    const result = createIframe(url, undefined, {
      presentation: config.presentation,
      corner: config.position,
    })
    iframe = result.iframe
    overlay = result.overlay
    bridge = createParentBridge(iframe, config.widgetBaseUrl)

    bridge.on("user_closed", () => {
      teardown()
      showLauncher()
    })
    bridge.on("session_expired", () => {
      teardown()
      showLauncher()
    })
  }

  async function handleLauncherClick() {
    if (opening || iframe) return
    opening = true
    button.style.opacity = "0.6"
    button.style.pointerEvents = "none"

    try {
      const auth = await authorize(config)
      if (!auth) {
        console.error("[ShowcaseSDK] authorize failed — widget not started")
        showLauncher()
        return
      }

      const file = await pickFile()
      if (!file) {
        showLauncher()
        return
      }

      const imageUrl = await uploadImageFile(file, auth.token, config.widgetBaseUrl)
      if (!imageUrl) {
        console.error("[ShowcaseSDK] image upload failed")
        showLauncher()
        return
      }

      const layout =
        config.layout ?? auth.defaultLayout ?? ("desktop" as EmbeddedLayout)

      hideLauncher()
      startEmbed(auth.token, imageUrl, layout)
    } catch (err) {
      console.error("[ShowcaseSDK] launcher error", err)
      showLauncher()
    } finally {
      opening = false
      button.style.opacity = ""
      button.style.pointerEvents = ""
    }
  }

  button.addEventListener("click", () => {
    void handleLauncherClick()
  })

  const attach = () => { document.body.appendChild(container) }
  if (document.body) {
    attach()
  } else {
    document.addEventListener("DOMContentLoaded", attach)
  }
}

export function findBootScript(
  currentScript: { getAttribute(name: string): string | null } | null,
): { getAttribute(name: string): string | null } | null {
  if (currentScript?.getAttribute("data-partner-slug")) return currentScript
  if (typeof document === "undefined") return null
  return document.querySelector("script[data-partner-slug]")
}

export function maybeAutoboot(
  script: { getAttribute(name: string): string | null } | null,
): void {
  const config = parseShowcaseConfig(findBootScript(script))
  if (!config) return

  const w = window as unknown as Record<string, unknown>
  if (w[BOOTED_FLAG]) return
  w[BOOTED_FLAG] = true

  bootShowcaseLauncher(config)
}
